/**
 * BuildTracker — renders a {@link BuildStatusView}: the "step n of N" counter
 * naming the current phase, its plain-language detail (round r of R on model
 * M, check k by name, quality pass p of P) and how long it has been running,
 * then the phase pills. Raw gate/typecheck output is NOT shown here — it lives
 * in the version history (owner ask 2026-07-23); a failed build shows only its
 * creator-readable reason. This is the "where is it right now" the Iterate
 * panel shows while a build runs (owner ask 2026-09-03).
 */
import { BUILD_PHASE_LABELS, type BuildStatusView, type StepState } from '../studio/buildStatus.js';

/** "12s" / "3m 05s" / "1h 02m" — coarse, for a live readout. */
export function formatElapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, '0')}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${String(m % 60).padStart(2, '0')}m`;
}

const ICON: Record<StepState, string> = {
  done: '✓',
  failed: '✕',
  active: '●',
  pending: '○',
  skipped: '–',
};

export function BuildTracker({ view, now }: { view: BuildStatusView; now?: number }) {
  const elapsed =
    !view.terminal && view.since !== null && now !== undefined ? formatElapsed(now - view.since) : '';
  return (
    <div className="build-tracker" data-testid="build-tracker">
      <p className="build-tracker__counter">
        <strong>
          {view.failed ? 'Stopped at step' : 'Step'} {view.step} of {view.stepCount}
        </strong>
        {' · '}
        {BUILD_PHASE_LABELS[view.phase]}
        {view.detail && <span className="muted"> · {view.detail}</span>}
        {elapsed && <span className="muted"> · {elapsed}</span>}
      </p>
      {view.failure && <p className="build-tracker__failure" data-testid="build-failure">{view.failure}</p>}
      <div className="tracker" role="list">
        {view.steps.map((s) => (
          <span key={s.phase} role="listitem" className={`tracker__step tracker__step--${s.state}`}>
            {ICON[s.state]} {s.label}
            {s.state === 'skipped' && <small>Not needed this time — {s.skipExplanation}</small>}
          </span>
        ))}
      </div>
    </div>
  );
}
